import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

export default function Header() {
  const [menuItems, setMenuItems] = useState([])
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/menu_items')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const sorted = data.sort((a, b) => (a.order || 0) - (b.order || 0))
          setMenuItems(sorted)
        }
      })
      .catch((err) => {
        console.error('Failed to fetch menu items from CMS, using fallback', err)
        setMenuItems([
          { href: '/', label: 'Forside', order: 1 },
          { href: '/om-os', label: 'Om os', order: 2 },
          { href: '#', label: 'Ydelser', order: 3 },
          { href: '/ydelser/tagarbejde', label: 'Tagarbejde', order: 4, parent_slug: 'ydelser' },
          { href: '/ydelser/renovering', label: 'Renovering', order: 5, parent_slug: 'ydelser' },
          { href: '/ydelser/tilbygning', label: 'Tilbygning', order: 6, parent_slug: 'ydelser' },
          { href: '/ydelser/nybyg', label: 'Nybyg', order: 7, parent_slug: 'ydelser' },
          { href: '/ydelser/nyt-gulv', label: 'Nyt gulv', order: 8, parent_slug: 'ydelser' },
          { href: '/ydelser/vinduer-og-doere', label: 'Vinduer og døre', order: 9, parent_slug: 'ydelser' },
          { href: '/galleri', label: 'Galleri', order: 10 },
          { href: '/kontakt', label: 'Kontakt', order: 11 }
        ])
      })
  }, [])

  useEffect(() => {
    setIsMobileMenuOpen(false)
    setIsDropdownOpen(false)
  }, [location])

  const mainItems = menuItems.filter((item) => !item.parent_slug)
  const serviceItems = menuItems.filter((item) => item.parent_slug === 'ydelser')

  const isLinkActive = (href) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  return (
    <header className="relative z-50 w-full bg-white">
      {/* Tier 1: Brand / Phone Bar */}
      <div className="relative w-full border-b border-zinc-200 bg-white h-24 md:h-28 overflow-hidden">
        {/* Full-bleed Wood Background on the left */}
        <div 
          className="absolute inset-y-0 left-0 w-[55%] sm:w-[55%] md:w-[60%] bg-zinc-950 bg-cover bg-center"
          style={{ 
            clipPath: 'polygon(0 0, 100% 0, 93% 100%, 0% 100%)',
            backgroundImage: 'linear-gradient(rgba(9, 9, 11, 0.3), rgba(9, 9, 11, 0.3)), url("https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/37f7f16d-7c9c-4f81-b10c-2a08ddb4d637/dark-wood-texture.jpg")'
          }}
        />
        
        {/* Content Container aligned to max-w-7xl */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex justify-between items-center">
          {/* Left Side: Logo (aligned with content container) */}
          <div className="flex items-center h-full">
            <Link to="/" className="flex items-center">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=320,quality=95,format=auto/assets/37f7f16d-7c9c-4f81-b10c-2a08ddb4d637/logo-9_1.png"
                alt="Hansen & Mortensen"
                className="h-12 sm:h-14 md:h-16 w-auto object-contain brightness-0 invert"
              />
            </Link>
          </div>
          
          {/* Right Side: Contact Block (White Base) */}
          <div className="hidden sm:flex flex-col justify-center items-end text-right bg-white select-none">
            <div className="inline-block border-b-2 border-red-700 pb-1">
              <a 
                href="tel:22115514" 
                className="text-lg sm:text-2xl md:text-3xl font-black tracking-tight text-zinc-950 hover:text-red-700 transition-colors"
              >
                22 11 55 14
              </a>
            </div>
            <p className="text-[9px] sm:text-xs text-zinc-600 tracking-wider mt-1.5 font-bold uppercase">
              Hansen & Mortensen Fredensborg
            </p>
          </div>
        </div>
      </div>

      {/* Tier 2: Navigation Bar */}
      <nav className="bg-zinc-100 border-y border-zinc-200 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-12">
          
          {/* Left: Desktop Navigation Links */}
          <div className="hidden min-[1024px]:flex items-center space-x-8 h-full">
            {mainItems.map((item) => {
              if (item.label === 'Ydelser') {
                return (
                  <div
                    key={item.label}
                    className="relative group flex items-center h-full cursor-pointer"
                    onMouseEnter={() => setIsDropdownOpen(true)}
                    onMouseLeave={() => setIsDropdownOpen(false)}
                  >
                    <button
                      className={`flex items-center gap-1.5 text-[13px] font-bold tracking-wide uppercase h-full transition-colors ${
                        location.pathname.startsWith('/ydelser')
                          ? 'text-red-700 border-b-2 border-red-700'
                          : 'text-zinc-800 hover:text-red-700'
                      }`}
                    >
                      <span>Ydelser</span>
                      <i className={`fa-solid fa-chevron-down text-[10px] text-zinc-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180 text-red-700' : ''}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                      <div className="absolute left-0 top-full z-50 w-60 bg-white border border-zinc-200 shadow-md rounded-none py-1">
                        {serviceItems.map((sub) => (
                          <Link
                            key={sub.href}
                            to={sub.href}
                            className={`block px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                              isLinkActive(sub.href)
                                ? 'bg-red-700 text-white'
                                : 'text-zinc-800 hover:bg-zinc-100 hover:text-red-700'
                            }`}
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )
              }

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`relative flex items-center h-full text-[13px] font-bold tracking-wide uppercase transition-colors px-1 ${
                    isLinkActive(item.href)
                      ? 'text-red-700 border-b-2 border-red-700'
                      : 'text-zinc-800 hover:text-red-700'
                  }`}
                >
                  {item.label}
                </Link>
              )
            })}
          </div>

          {/* Right: Mobile Trigger */}
          <div className="flex items-center justify-between w-full min-[1024px]:w-auto h-full">
            {/* Mobile-only elements */}
            <div className="flex w-full items-center justify-between min-[1024px]:hidden">
              <a
                href="tel:22115514"
                className="flex items-center gap-2 text-xs font-bold text-red-700 uppercase tracking-wider"
              >
                <i className="fa-solid fa-phone" />
                <span>Ring op: 22 11 55 14</span>
              </a>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-zinc-900 hover:text-red-700 transition-colors focus:outline-none"
                aria-label="Menu"
              >
                <i className={`fa-solid ${isMobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-lg`} />
              </button>
            </div>
          </div>

        </div>
      </nav>

      {/* Mobile Dropdown Menu */}
      {isMobileMenuOpen && (
        <div className="border-t border-zinc-200 bg-white py-4 shadow-inner min-[1024px]:hidden">
          <nav className="flex flex-col space-y-1 px-4">
            {mainItems.map((item) => {
              if (item.label === 'Ydelser') {
                return (
                  <div key={item.label} className="flex flex-col space-y-1 py-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-zinc-400 px-2 py-1">
                      Ydelser
                    </span>
                    <div className="pl-4 flex flex-col space-y-1 border-l-2 border-zinc-200">
                      {serviceItems.map((sub) => (
                        <Link
                          key={sub.href}
                          to={sub.href}
                          className={`block px-2 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                            isLinkActive(sub.href)
                              ? 'text-red-700 font-extrabold'
                              : 'text-zinc-800 hover:text-red-700'
                          }`}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )
              }

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`block px-2 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                    isLinkActive(item.href)
                      ? 'text-red-700 font-extrabold'
                      : 'text-zinc-800 hover:text-red-700'
                  }`}
                >
                  {item.label}
                </Link>
              )
            })}

          </nav>
        </div>
      )}
    </header>
  )
}
